import React from 'react';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .static-page {
    font-family: 'Inter', system-ui, sans-serif;
    background: #0a0f1e;
    min-height: 100vh;
    color: #e2e8f0;
  }

  .static-hero {
    background: linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #0a0f1e 100%);
    padding: 80px 24px 70px;
    text-align: center;
    position: relative;
    overflow: hidden;
    border-bottom: 1px solid rgba(99,102,241,0.15);
  }

  .static-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 70% 60% at 50% -10%, rgba(99,102,241,0.18) 0%, transparent 70%);
    pointer-events: none;
  }

  .static-label {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #a5b4fc;
    background: rgba(99,102,241,0.1);
    border: 1px solid rgba(99,102,241,0.25);
    border-radius: 100px;
    padding: 4px 14px;
    margin-bottom: 20px;
  }

  .static-hero h1 {
    font-size: clamp(2rem, 5vw, 3.2rem);
    font-weight: 900;
    color: #f1f5f9;
    margin: 0 0 16px;
    letter-spacing: -0.03em;
  }

  .static-hero h1 span {
    background: linear-gradient(135deg, #818cf8, #6366f1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .static-hero-sub {
    font-size: 1.05rem;
    color: #64748b;
    max-width: 540px;
    margin: 0 auto;
    line-height: 1.7;
  }

  .static-meta {
    font-size: 12px;
    color: #475569;
    margin-top: 12px;
  }

  .static-body {
    max-width: 800px;
    margin: 0 auto;
    padding: 64px 24px 100px;
  }

  .static-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.12);
    border-radius: 20px;
    padding: 40px;
    margin-bottom: 28px;
    transition: border-color 0.3s;
  }

  .static-card:hover { border-color: rgba(99,102,241,0.25); }

  .static-card h2 {
    font-size: 1.15rem;
    font-weight: 800;
    color: #f1f5f9;
    margin: 0 0 16px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .card-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px; height: 34px;
    background: rgba(99,102,241,0.15);
    border-radius: 9px;
    font-size: 16px;
    flex-shrink: 0;
  }

  .static-card h3 {
    font-size: 1rem;
    font-weight: 700;
    color: #c7d2fe;
    margin: 20px 0 8px;
  }

  .static-card p, .static-card li {
    font-size: 0.93rem;
    line-height: 1.8;
    color: #94a3b8;
    margin: 0 0 10px;
  }

  .static-card ul {
    padding-left: 20px;
    margin: 8px 0;
  }

  .static-card li::marker { color: #6366f1; }
  .static-card li { margin-bottom: 6px; }
  .static-card strong { color: #c7d2fe; }

  @media (max-width: 560px) {
    .static-card { padding: 28px 20px; }
  }
`;

const Privacy = () => (
  <div className="static-page">
    <style>{STYLES}</style>

    <div className="static-hero">
      <span className="static-label">Legal</span>
      <h1>Privacy <span>Policy</span></h1>
      <p className="static-hero-sub">
        We respect your data and your privacy. Here's exactly what we collect, why, and how we protect it.
      </p>
      <p className="static-meta">Last updated: March 5, 2025</p>
    </div>

    <div className="static-body">

      <div className="static-card">
        <h2><span className="card-icon">📋</span>Information We Collect</h2>
        <p>We collect only what we need to provide you with a great service.</p>
        <h3>Personal Information</h3>
        <ul>
          <li>Email address and username (when you create an account)</li>
          <li>Password (stored as a salted hash — never in plain text)</li>
          <li>Payment information (for premium plans — processed by Stripe, not stored by us)</li>
        </ul>
        <h3>Usage Information</h3>
        <ul>
          <li>Links you create, edit, and manage</li>
          <li>Click analytics and tracking data associated with your links</li>
          <li>Device type, browser, and operating system of link visitors</li>
          <li>Country and city-level location of link visitors (from IP address)</li>
          <li>Referrer sources (where clicks came from)</li>
        </ul>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">⚙️</span>How We Use Your Information</h2>
        <ul>
          <li>To create and maintain your account and links</li>
          <li>To provide click analytics on your dashboard</li>
          <li>To process payments and manage plan subscriptions</li>
          <li>To send transactional emails (account verification, password reset)</li>
          <li>To analyze aggregate usage patterns and improve the platform</li>
          <li>To detect and prevent abuse, spam, and malicious links</li>
        </ul>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">🤝</span>Data Sharing</h2>
        <p>
          We <strong>do not sell, trade, or rent</strong> your personal information to third parties — ever.
          We may share your data only in these specific circumstances:
        </p>
        <ul>
          <li>With your explicit written consent</li>
          <li>To comply with a valid legal obligation or court order</li>
          <li>To protect the rights, property, or safety of RDRTO or its users</li>
          <li>With trusted service providers (e.g., payment processor, hosting) who are contractually bound to keep data confidential</li>
        </ul>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">🔒</span>Data Security</h2>
        <p>
          We implement industry-standard security measures to protect your information:
        </p>
        <ul>
          <li>HTTPS / TLS encryption on all connections</li>
          <li>Bcrypt password hashing with per-user salts</li>
          <li>JWT-based authentication with short-lived tokens</li>
          <li>Regular security audits and dependency updates</li>
          <li>Isolated database access with least-privilege principles</li>
        </ul>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">👤</span>Your Rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
          <li><strong>Correction</strong> — update inaccurate or incomplete data via your account settings</li>
          <li><strong>Deletion</strong> — request that we permanently delete your account and associated data</li>
          <li><strong>Portability</strong> — export your link data at any time from your dashboard</li>
          <li><strong>Opt-out</strong> — unsubscribe from marketing emails at any time</li>
        </ul>
        <p style={{ marginTop: 16 }}>
          To exercise any of these rights, email us at <a href="mailto:admin@rdrto.com" style={{ color: '#a5b4fc', textDecoration: 'none' }}>admin@rdrto.com</a>.
          We respond within 7 business days.
        </p>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">🍪</span>Cookies</h2>
        <p>
          We use only essential session cookies required for authentication. We do not use
          third-party advertising cookies or cross-site tracking. You can disable cookies in
          your browser settings, though this will prevent you from staying logged in.
        </p>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">🏷️</span>Brand Namespace &amp; Reserved Name Policy</h2>
        <p>
          To protect users, intellectual property rights, and the integrity of the platform,
          certain brand names and keywords are restricted. Users on Pro and Business plans
          may create a brand namespace subject to the rules below.
        </p>

        <h3>Prohibited Brand Names</h3>
        <p>Users may not register or use brand names that fall into any of the following categories:</p>

        <p><strong>System &amp; Infrastructure Terms</strong> — Names that interfere with platform routing or internal operations, including but not limited to:
          <code style={{ display: 'inline-block', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 6, padding: '2px 8px', fontSize: 12, color: '#a5b4fc', margin: '6px 0' }}>
            admin, api, www, app, dashboard, login, support, cdn, static, mail, auth, system
          </code>
          and any other reserved internal system keyword.
        </p>

        <p><strong>Trademark &amp; Impersonation</strong> — Names that infringe on registered trademarks, impersonate well-known companies, public figures, or brands, or create a likelihood of confusion with established entities. This includes major global brands, government agencies, financial institutions, payment processors, and technology platforms.</p>

        <p><strong>Government &amp; Authority Representation</strong> — Names implying affiliation with government entities, law enforcement, tax authorities, regulatory agencies, or public institutions are strictly prohibited unless legally verified.</p>

        <p><strong>Financial or High-Risk Misrepresentation</strong> — Names suggesting banking services, investment platforms, cryptocurrency exchanges, refund/claim services, or account verification portals may be restricted, flagged for review, or removed.</p>

        <p><strong>Offensive or Harmful Content</strong> — Names containing hate speech, harassment, threats, adult or explicit content, or references to illegal activity are strictly prohibited.</p>

        <h3>Brand Name Requirements</h3>
        <p>All brand names must:</p>
        <ul>
          <li>Be between 3–30 characters</li>
          <li>Contain only lowercase letters (a–z), numbers (0–9), and hyphens (–)</li>
          <li>Not consist solely of numbers</li>
          <li>Be globally unique on the platform</li>
        </ul>
        <p>We reserve the right to normalize, reject, suspend, or remove brand names that violate these requirements.</p>

        <h3>Review &amp; Enforcement</h3>
        <p>We may manually review brand registrations, reject or revoke brand namespaces at our sole discretion, suspend associated links, require proof of trademark ownership, and modify or disable brand-based URLs without prior notice in cases of abuse.</p>

        <h3>Intellectual Property Responsibility</h3>
        <p>Users are solely responsible for ensuring their chosen brand name does not infringe third-party intellectual property, does not violate applicable laws, and does not mislead or deceive others. RDRTO is not liable for user-submitted brand names.</p>

        <h3>Right to Reclaim Reserved Names</h3>
        <p>We reserve the right to reclaim inactive brand names, reserve brand names for future platform features, and reassign or disable brand names in case of disputes.</p>

        <h3>Reporting &amp; Disputes</h3>
        <p>
          Trademark owners or authorized representatives may contact{' '}
          <a href="mailto:admin@rdrto.com" style={{ color: '#a5b4fc', textDecoration: 'none' }}>admin@rdrto.com</a>
          {' '}to report brand misuse or intellectual property violations.
        </p>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">📬</span>Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy, please contact us at{' '}
          <a href="mailto:admin@rdrto.com" style={{ color: '#a5b4fc', textDecoration: 'none' }}>admin@rdrto.com</a>.
          We aim to respond within 24 hours.
        </p>
      </div>

    </div>
  </div>
);

export default Privacy;
